import React, { useState, useMemo } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TablePagination,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import colors from "../../Constants/colors";
import { useTranslation } from "react-i18next";
import LogCard from "../Common/LogCard/LogCard";
import { useUserEvaluationRequests } from "../../Query/useUserEvaluationRequests";
import { userEvaluationRequestsParams } from "../../Models/userEvaluationRequests";
import { User } from "../../Models/user";
import { Company } from "../../Models/company";
import { useTableParams } from "../../Core/useTableParams";
import ActionLogFilters from "./ActionLogFilters";
import { useUsers } from "../../Query/useUsers";
import { useCompaniesAll } from "../../Query/useCompanies";
import { useDebouncedCallback } from "../../Hooks/useDebouncedCallback";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import color from "../../Constants/colors";

const ActionLog: React.FC = () => {
  const { t } = useTranslation("actionLog");
  const { colors: workspace } = useWorkspaceColors();

  const [userSearch, setUserSearch] = useState("");
  const [companySearch, setCompanySearch] = useState("");

  const { page, setPage } = useTableParams({ defaultPage: 1 });
  const PAGE_SIZE = 20;

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  const debouncedSetUserSearch = useDebouncedCallback((value: string) => {
    setUserSearch(value);
    setPage(1);
  }, 1000);

  const debouncedSetCompanySearch = useDebouncedCallback((value: string) => {
    setCompanySearch(value);
    setPage(1);
  }, 1000);

  const { data: usersData, isFetching: usersLoading } = useUsers(
    1,
    50,
    userSearch,
    "name",
    "asc",
  );

  const { data: companiesData, isFetching: companiesLoading } = useCompaniesAll(
    1,
    50,
    "name",
    companySearch,
    selectedUser?.id,
    { enabled: !!selectedUser },
  );

  const queryParams: userEvaluationRequestsParams = useMemo(() => {
    return {
      page,
      user_id: selectedUser?.id.toString(),
      company_id: selectedCompany?.id.toString(),
    };
  }, [page, selectedUser, selectedCompany]);

  const {
    data: logData,
    isLoading: logsLoading,
    refetch: refetchLogs,
    isFetching: logsFetching,
    dataUpdatedAt,
  } = useUserEvaluationRequests(queryParams);

  const lastRefreshedAt = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : null;

  const handleUserChange = (
    _e: React.SyntheticEvent,
    newValue: User | null,
  ) => {
    setSelectedUser(newValue);
    setSelectedCompany(null);
    setPage(1);
  };

  const handleCompanyChange = (
    _e: React.SyntheticEvent,
    newValue: Company | null,
  ) => {
    setSelectedCompany(newValue);
    setPage(1);
  };

  return (
    <Box
      sx={{ minHeight: "calc(100vh - 64px)", backgroundColor: colors.gray50 }}
    >
      <Box sx={{ maxWidth: "1280px", mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: colors.white,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <HistoryOutlinedIcon
                sx={{ fontSize: 32, color: workspace.primary_dark }}
              />
              <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: workspace.primary_dark }}
                >
                  {t("actionLogTittle")}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.gray500 }}>
                  {t("evaluationTableTittle")}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1}>
              {lastRefreshedAt && (
                <Typography variant="caption" sx={{ color: colors.gray500 }}>
                  {t("logs.refreshedAt", { time: lastRefreshedAt })}
                </Typography>
              )}
              <Tooltip title={t("logs.refresh")}>
                <IconButton
                  onClick={() => refetchLogs()}
                  disabled={logsFetching}
                  size="small"
                  sx={{ color: workspace.primary_dark }}
                >
                  {logsFetching && !logsLoading ? (
                    <CircularProgress
                      size={18}
                      sx={{ color: workspace.accent }}
                    />
                  ) : (
                    <RefreshIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: colors.white,
          }}
        >
          <ActionLogFilters
            users={usersData?.users || []}
            selectedUser={selectedUser}
            handleUserChange={handleUserChange}
            onUserSearch={debouncedSetUserSearch}
            companies={companiesData?.companies || []}
            selectedCompany={selectedCompany}
            handleCompanyChange={handleCompanyChange}
            onCompanySearch={debouncedSetCompanySearch}
            usersLoading={usersLoading}
            companiesLoading={companiesLoading}
          />
        </Paper>

        <Box sx={{ position: "relative", minHeight: 200 }}>
          {logsLoading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(249,250,251,0.7)",
                zIndex: 10,
                borderRadius: 2,
              }}
            >
              <CircularProgress sx={{ color: workspace.accent }} />
            </Box>
          )}
          <Stack spacing={2}>
            {(logData?.user_evaluation_requests ?? []).length === 0 &&
              !logsLoading && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 6,
                    textAlign: "center",
                    borderRadius: 2,
                    border: `1px solid ${colors.gray200}`,
                    borderLeft: `4px solid ${workspace.primary_dark}`,
                    background: colors.white,
                    color: colors.gray500,
                  }}
                >
                  <Typography
                    sx={{ fontSize: "0.875rem", color: color.primary_dark }}
                  >
                    {t("logs.noLogsDisplay")}
                  </Typography>
                </Paper>
              )}
            {logData?.user_evaluation_requests?.map((req) => (
              <LogCard key={req.id} evaluationRequest={req} />
            ))}
          </Stack>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <TablePagination
              component="div"
              count={logData?.total_count ?? 0}
              page={page - 1}
              onPageChange={(_, p) => setPage(p + 1)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[]}
              labelRowsPerPage=""
              sx={{
                "& .MuiTablePagination-displayedRows": {
                  color: colors.gray600,
                  fontSize: "0.8rem",
                },
                "& .MuiIconButton-root": {
                  color: workspace.primary_dark,
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ActionLog;
